import React, { useEffect, useRef } from 'react';

const STAGES = {
  recording: 'Listening',
  processing: 'Matching fingerprints',
};

// Live reactive waveform ring + stage ticker for the Track ID scan.
export const ScanStage = ({ stream, recordingState, countdown, quickSeconds = 6, fullSeconds = 12 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!stream || recordingState !== 'recording' || !canvasRef.current) return undefined;
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (_) {
      return undefined;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.82;
    let source;
    try {
      source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
    } catch (_) {
      ctx.close();
      return undefined;
    }
    const data = new Uint8Array(analyser.frequencyBinCount);
    const canvas = canvasRef.current;
    const g = canvas.getContext('2d');
    const draw = () => {
      analyser.getByteFrequencyData(data);
      const { width, height } = canvas;
      g.clearRect(0, 0, width, height);
      const bars = 40;
      const gap = 3;
      const bw = (width - gap * (bars - 1)) / bars;
      for (let i = 0; i < bars; i++) {
        const idx = Math.floor((i / bars) * data.length * 0.7);
        const v = data[idx] / 255;
        const h = Math.max(3, v * height * 0.95);
        const x = i * (bw + gap);
        const hue = 185 + v * 150;
        g.fillStyle = `hsla(${hue}, 100%, ${55 + v * 20}%, ${0.55 + v * 0.45})`;
        g.beginPath();
        g.roundRect(x, (height - h) / 2, bw, h, 2);
        g.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(rafRef.current);
      try { source.disconnect(); } catch (_) {}
      ctx.close().catch(() => {});
    };
  }, [stream, recordingState]);

  if (recordingState !== 'recording' && recordingState !== 'processing') return null;
  const elapsed = fullSeconds - (countdown ?? fullSeconds);
  const pastQuick = elapsed >= quickSeconds;

  return (
    <div className="scan-stage" data-testid="scan-stage">
      {recordingState === 'recording' && (
        <canvas ref={canvasRef} width={320} height={56} className="scan-waveform" data-testid="scan-waveform" />
      )}
      {recordingState === 'processing' && (
        <div className="scan-matching" data-testid="scan-matching">
          <span /><span /><span /><span /><span />
        </div>
      )}
      <div className="scan-ticker">
        <span className={`scan-step ${recordingState === 'recording' ? 'active' : 'done'}`}>
          {STAGES.recording}{recordingState === 'recording' ? ` · ${countdown}s` : ''}
        </span>
        <span className={`scan-step ${recordingState === 'recording' && pastQuick ? 'active' : recordingState === 'processing' ? 'done' : ''}`}>
          Quick match
        </span>
        <span className={`scan-step ${recordingState === 'processing' ? 'active' : ''}`}>
          {STAGES.processing}
        </span>
      </div>
      {recordingState === 'recording' && (
        <p className="scan-hint">{pastQuick ? 'Locking in… hold steady near the speaker' : 'Point at the speaker · avoid talking over it'}</p>
      )}
    </div>
  );
};

export default ScanStage;
